import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ACCESS_KEY = "peeping_at";
const REFRESH_KEY = "peeping_rt";

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
});

// Bearer fallback: Safari (iPad/iPhone) blocks cookies inside embedded previews,
// so the session tokens are also kept in storage and sent as Authorization.
export const tokenStore = {
  save(data) {
    if (data?.access_token) window.localStorage.setItem(ACCESS_KEY, data.access_token);
    if (data?.refresh_token) window.localStorage.setItem(REFRESH_KEY, data.refresh_token);
  },
  clear() {
    window.localStorage.removeItem(ACCESS_KEY);
    window.localStorage.removeItem(REFRESH_KEY);
  },
  access: () => window.localStorage.getItem(ACCESS_KEY),
  refresh: () => window.localStorage.getItem(REFRESH_KEY),
};

export function stripTokens(data) {
  if (!data) return data;
  const { access_token, refresh_token, ...user } = data;
  return user;
}

api.interceptors.request.use((config) => {
  const t = tokenStore.access();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// DEV PREVIEW MODE: creator/admin sees the subscriber experience. Backend ignores it for subscribers.
if (typeof window !== "undefined" && window.localStorage.getItem("peeping_preview") === "1") {
  api.defaults.headers.common["X-Peeping-Preview"] = "subscriber";
}

const NO_RETRY = ["/auth/login", "/auth/refresh", "/auth/logout", "/auth/register"];
let refreshPromise = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const url = original?.url || "";
    if (error.response?.status === 401 && original && !original._retry && !NO_RETRY.some((p) => url.includes(p))) {
      original._retry = true;
      try {
        refreshPromise = refreshPromise || api.post("/auth/refresh", { refresh_token: tokenStore.refresh() });
        const { data } = await refreshPromise;
        refreshPromise = null;
        tokenStore.save(data);
        return api(original);
      } catch (e) {
        refreshPromise = null;
        if (e.response?.status === 401) {
          tokenStore.clear();
          window.dispatchEvent(new Event("peeping-auth-expired"));
        } else {
          return Promise.reject(e);
        }
      }
    }
    return Promise.reject(error);
  }
);

// media_url from the backend looks like "/api/media/<id>"; strip the baseURL's /api prefix.
export const toApiPath = (url) => (url ? url.replace(/^\/api/, "") : "");

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const homeFor = (user) =>
  user?.role === "super_admin" ? "/admin" : user?.role === "creator" ? "/creator/dashboard" : "/app/discover";
export const isOwnerReview = (user) => user?.role === "platform_owner_review";
